import { Alert, Box, CircularProgress } from "@mui/material";
import { useQuery, useQueries } from "@tanstack/react-query";
import React, { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import { practiceProfileApi, practiceUsersApi } from "apis";
import { PracticeUserBranchesSection } from "components/features/practiceManagement";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import { BranchDto } from "libs/cellera-sdk";
import { useCelleraTheme } from "theme/useCelleraTheme";

const PracticeDetailPage: React.FC = () => {
  const { practiceId } = useParams<{ practiceId: string }>();
  const navigate = useNavigate();
  const { colors } = useCelleraTheme();

  const {
    data: practiceDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
    refetch: refetchDetails,
  } = useQuery({
    queryKey: ["practice-details", practiceId],
    queryFn: async () => {
      const response = await practiceProfileApi.getPracticeDetailsForPatient({
        practiceId: practiceId || "",
      });
      return response as any;
    },
    enabled: Boolean(practiceId),
  });

  const branchesFromDetails = useMemo<BranchDto[]>(() => {
    if (!practiceDetails) {
      return [];
    }

    if (Array.isArray(practiceDetails)) {
      const branches = practiceDetails
        .filter((item: any) => item.address)
        .map((item: any) => ({
          id: item.id,
          address: item.address,
          street: item.street || "",
          city: item.city || "",
          state: item.state || "",
          zip: item.zip || "",
          emailAddress: item.emailAddress,
          primaryPhoneNumber: item.primaryPhoneNumber,
          faxNumber: item.faxNumber,
          staffCount: item.staffCount || 0,
        }));
      return branches;
    }

    if (practiceDetails.branches && Array.isArray(practiceDetails.branches)) {
      const branches = practiceDetails.branches.map((branch: any) => ({
        id: branch.id,
        address: branch.address,
        street: branch.street || "",
        city: branch.city || "",
        state: branch.state || "",
        zip: branch.zip || "",
        emailAddress: branch.emailAddress,
        primaryPhoneNumber: branch.primaryPhoneNumber,
        faxNumber: branch.faxNumber,
        staffCount: branch.staffCount || 0,
      }));
      return branches;
    }

    if (practiceDetails.data?.branches) {
      const branches = practiceDetails.data.branches.map((branch: any) => ({
        id: branch.id,
        address: branch.address,
        street: branch.street || "",
        city: branch.city || "",
        state: branch.state || "",
        zip: branch.zip || "",
        emailAddress: branch.emailAddress,
        primaryPhoneNumber: branch.primaryPhoneNumber,
        faxNumber: branch.faxNumber,
        staffCount: branch.staffCount || 0,
      }));
      return branches;
    }

    return [];
  }, [practiceDetails]);

  const branchQueries = useQueries({
    queries: branchesFromDetails.map((branch) => ({
      queryKey: ["branch-staff-count", practiceId, branch.id],
      queryFn: async () => {
        if (!branch.id || !practiceId) return { count: 0 };
        try {
          const response = await practiceUsersApi.listPracticeUsersForAdmin({
            practiceId: practiceId,
            branchId: branch.id,
            page: 1,
            pageSize: 1,
          });
          return { count: response.pagination?.total || 0 };
        } catch {
          return { count: 0 };
        }
      },
      enabled: Boolean(practiceId && branch.id),
    })),
  });

  const branchesWithStaffCounts = useMemo<BranchDto[]>(() => {
    return branchesFromDetails.map((branch, index) => {
      const staffCount =
        branchQueries[index]?.data?.count || branch.staffCount || 0;
      return {
        ...branch,
        staffCount,
      };
    });
  }, [branchesFromDetails, branchQueries]);

  const practiceName = useMemo(() => {
    if (practiceDetails?.practice?.name) return practiceDetails.practice.name;
    if (practiceDetails?.practiceName) return practiceDetails.practiceName;
    if (practiceDetails?.name) return practiceDetails.name;
    return "Practice";
  }, [practiceDetails]);

  const handleBack = useCallback(() => {
    navigate(EPath.PRACTICE_MANAGEMENT);
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    refetchDetails();
  }, [refetchDetails]);

  const handleBranchUserNavigate = useCallback(
    (userId: string) => {
      navigate(EPath.PRACTICE_USER_DETAIL.replace(":practiceUserId", userId));
    },
    [navigate],
  );

  const isLoading = isLoadingDetails;
  const error = detailsError;

  const renderContent = () => {
    if (error) {
      return (
        <Alert
          severity="error"
          action={
            <Box
              component="button"
              onClick={() => refetchDetails()}
              style={{
                background: "none",
                border: "none",
                color: colors.primary.main,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Retry
            </Box>
          }
        >
          {error instanceof Error
            ? error.message
            : "Failed to load practice information"}
        </Alert>
      );
    }

    if (!practiceDetails) {
      return <Alert severity="info">Practice not found.</Alert>;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          p: 3,
        }}
      >
        <Box>
          <Box sx={{ mb: 2 }}>
            <Box
              component="h2"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "#111827",
                mb: 1,
              }}
            >
              {practiceName}
            </Box>
          </Box>
        </Box>

        <PracticeUserBranchesSection
          branches={branchesWithStaffCounts}
          onNavigateToUser={handleBranchUserNavigate}
        />
      </Box>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Practice Details">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={48} />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Practice Details"
      breadcrumbs={[
        {
          label: "Practice Management",
          href: EPath.PRACTICE_MANAGEMENT,
          order: 0,
        },
        {
          label: "Practice Management",
          href: EPath.PRACTICE_MANAGEMENT,
          order: 1,
        },
      ]}
      lastBreadcrumb="Practice Details"
      onBack={handleBack}
      onRefresh={handleRefresh}
    >
      <Box sx={{ p: 2 }}>{renderContent()}</Box>
    </DashboardLayout>
  );
};

export default PracticeDetailPage;
